import React, { useState, useCallback, useMemo, useEffect } from "react";
import { View, Linking } from "react-native";
import { Controller } from "react-hook-form";
import { StepShell } from "../components/StepShell";
import { AppTextInput } from "@/components/ui/TextInput";
import { FileUpload } from "@/components/ui/FileUpload";
import { PhoneInput, type CountryOption } from "@/components/ui/PhoneInput";
import { SelectField } from "@/components/ui/SelectField";
import { CountryPicker } from "@/components/ui/CountryPicker";
import { usePreVettingStep1Form } from "../hooks/usePreVettingStep1Form";
import { useI18n } from "@/context/I18nContext";
import { WORLD_COUNTRIES } from "@/constants/countries";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export default function PreVettingStep1Screen() {
  const { t } = useI18n();

  const idTypeOptions = useMemo(
    () => [
      { label: t("preVetting.step1.idType.nationalId"), value: "national_id" },
      { label: t("preVetting.step1.idType.passport"), value: "passport" },
      { label: t("preVetting.step1.idType.other"), value: "other" },
    ],
    [t]
  );

  const {
    form,
    onFieldBlur,
    handleNext,
    initialDialCode,
    setDialCode: setHookDialCode,
    idDocumentFile,
    idDocumentUploadState,
    idDocumentUploadError,
    handleIdDocumentFile,
    handleIdDocumentRemove,
    wechatQrFile,
    wechatQrUploadState,
    wechatQrUploadError,
    handleWechatQrFile,
    handleWechatQrRemove,
  } = usePreVettingStep1Form();

  const { errors } = form.formState;

  const ID_TYPE_OPTIONS = useMemo(
    () => [
      { label: t("preVetting.step1.idType.nationalId"), value: "national_id" },
      { label: t("preVetting.step1.idType.passport"), value: "passport" },
      { label: t("preVetting.step1.idType.other"), value: "other" },
    ],
    [t]
  );

  // UI-only state for the phone country picker
  const [dialCode, setDialCode] = useState(initialDialCode);
  const [countryFlag, setCountryFlag] = useState(() => {
    const match = WORLD_COUNTRIES.find((c) => c.dialCode === initialDialCode);
    return match?.flag ?? "🇨🇳";
  });
  const [nationalityFlag, setNationalityFlag] = useState("");

  // Sync UI when the hook restores a dial code from the saved draft
  useEffect(() => {
    setDialCode(initialDialCode);
    const match = WORLD_COUNTRIES.find((c) => c.dialCode === initialDialCode);
    if (match) setCountryFlag(match.flag);
  }, [initialDialCode]);

  const handleCountrySelect = useCallback(
    (country: CountryOption) => {
      setDialCode(country.dialCode);
      setCountryFlag(country.flag);
      setHookDialCode(country.dialCode);
    },
    [setHookDialCode],
  );

  return (
    <StepShell
      step={1}
      onNext={handleNext}
      nextLabel="preVetting.step1.nextLabel"
      iconName="account-circle"
      title="preVetting.step1.title"
      subtitle="preVetting.step1.subtitle"
    >
      <View className="items-start">
        <View className="tablet:flex-row tablet:gap-4 w-full">
          <View className="flex-1">
            <Controller
              control={form.control}
              name="legal_name_chinese"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  required
                  label="preVetting.step1.legalNameChinese"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  error={errors.legal_name_chinese?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>

          <View className="flex-1">
            <Controller
              control={form.control}
              name="legal_name_english"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  required
                  label="preVetting.step1.legalNameEnglish"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  error={errors.legal_name_english?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>
        </View>

        <Controller
          control={form.control}
          name="nationality"
          render={({ field: { value, onChange } }) => (
            <CountryPicker
              required
              label="preVetting.step1.nationality"
              countryName={value ?? ""}
              countryFlag={nationalityFlag}
              onCountrySelect={(country) => {
                onChange(country.name);
                setNationalityFlag(country.flag);
              }}
              error={errors.nationality?.message as TranslationKeys | undefined}
            />
          )}
        />

        <View className="tablet:flex-row tablet:gap-4 w-full">
          <View className="flex-1">
            <Controller
              control={form.control}
              name="role_in_company"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  required
                  label="preVetting.step1.roleInCompany"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  error={errors.role_in_company?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>

          <View className="flex-1">
            <Controller
              control={form.control}
              name="department"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  required
                  label="preVetting.step1.department"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  error={errors.department?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>
        </View>

        <View className="tablet:flex-row tablet:gap-4 w-full">
          <View className="flex-1">
            <Controller
              control={form.control}
              name="contact_phone"
              render={({ field: { value, onChange, onBlur } }) => (
                <PhoneInput
                  required
                  label="preVetting.step1.contactPhone"
                  dialCode={dialCode}
                  countryFlag={countryFlag}
                  onCountrySelect={handleCountrySelect}
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  keyboardType="phone-pad"
                  error={errors.contact_phone?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>

          <View className="flex-1">
            <Controller
              control={form.control}
              name="contact_email"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  label="preVetting.step1.contactEmail"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  error={errors.contact_email?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>
        </View>

        <Controller
          control={form.control}
          name="legal_representative_id_type"
          render={({ field: { value, onChange } }) => (
            <SelectField
              label="preVetting.step1.idType"
              options={idTypeOptions}
              value={value}
              onChange={onChange}
              placeholder="preVetting.step1.idType"
              required
              error={errors.legal_representative_id_type?.message as TranslationKeys | undefined}
            />
          )}
        />

        <FileUpload
          required
          label="preVetting.step1.idDocument"
          onFile={handleIdDocumentFile}
          uploadState={idDocumentUploadState}
          currentFile={idDocumentFile}
          onViewFile={idDocumentFile ? () => Linking.openURL(idDocumentFile.uri) : undefined}
          onRemoveFile={handleIdDocumentRemove}
          error={
            idDocumentUploadError ??
            (errors.legal_representative_id_document?.message as TranslationKeys | undefined)
          }
        />

        <FileUpload
          label="preVetting.step1.wechatQr"
          onFile={handleWechatQrFile}
          uploadState={wechatQrUploadState}
          currentFile={wechatQrFile}
          onViewFile={wechatQrFile ? () => Linking.openURL(wechatQrFile.uri) : undefined}
          onRemoveFile={handleWechatQrRemove}
          error={
            wechatQrUploadError ?? (errors.wechat_qr_string?.message as TranslationKeys | undefined)
          }
        />
      </View>
    </StepShell>
  );
}
